// `/delete-account` — DELETING THE ACCOUNT, THROUGH THE ONE PURGE THE APP USES (WEB-PAY-1; WEB-PARITY-7 DA-1, DA-2; ACC-3b).
//
// ONE ROUTE TO DELETION, AND IT IS THE SERVER'S. `v2Credits` `deleteAccount` calls v1's `deleteUserData` — the purge the
// iOS app's own deletion reaches — which removes the ledger, the grants, v2's store, the web purchase records and every
// collection `deletionReachesEveryCollection.test.ts` classifies. This screen adds no rule of its own: it shows what the
// server holds, asks the same question the app asks, and calls the server.
//
// THE APP'S ACCOUNT SCREEN (AccountManagementView.swift, DA-2): titled "Account" (:97), "Delete account" (:73) with the
// app's footer under it (:83, DA-1), and the same two-step confirmation — "Delete your account?" (:107) with the app's
// message (WEB-COPY-1 item 10), "Delete" and "Cancel" (:108, :116). The app's signed-in block and "Sign out" are the
// website's `/account/settings` (ACC-3b), with the server's WEB_ACCOUNTS_ENABLED on; with it off the website has no
// sign-in, so this page is the app's delete section alone, as it was.
//
// UNUSED CREDITS ARE NOT REFUNDED, AND THIS SCREEN MUST NOT SAY OTHERWISE (Tyler, 2026-09-28). When the account holds
// credits, the screen shows how many, from the server, and the approved Refund Policy's own sentence (§9; WEB-COPY-1 item
// 9). WITH THE SWITCH ON AND AN ACCOUNT SIGNED IN, ONE ACCOUNT HOLDS THE CREDITS IN BOTH PLACES (Tyler, 2026-09-30: "one
// account everywhere"), so items 9 and 10 and the app's footer become K5, K6 and K12 — which say "everywhere", because
// the purge reaches the one ledger the app reads too. With the switch off, or for a look-around, the words are today's:
// a look-around's balance is this website's alone.
//
// AND THEN THE SIGN-IN ITSELF GOES, IN iOS's ORDER (WEB-PAY-2, F-WP-2; ACC-1). With the switch on and an account signed
// in, the owner re-enters first (C6: the password, or Apple's window — whose fresh token is the one Apple's revocation
// needs), Apple's token is revoked BEFORE the server is asked (`revokeAppleBeforeDeletion`, F-AC-7), then the purge, and
// the server answers `signInDeleted` — passed to `endAfterDeletion`, which then only signs this browser out. With the
// switch off the order is today's: the purge, then the browser deletes the Firebase user itself. A refused purge ends
// nothing. The outcome is on the page as `data-auth-ended`, never as a sentence, and Continue loads the app afresh.

import { useState } from "react";
import type { AuthEnding, AuthRefusal } from "../services";
import { signedIn, useWebAccounts } from "../app/accounts";
import { useSession } from "../app/session";
import { useAction, useCall } from "../app/useCall";
import { HeldLine } from "../components/copy";
import { afterText } from "../components/afterFilingCopy";
import { Actions } from "../components/layout";
import { Refusal } from "../components/Refusal";
import { AppleButton, AuthError, PasswordField, Say } from "./account/parts";
import { deleteWarningTexts, hashesOf } from "../app/shownCopy";

export function DeleteAccount() {
  const session = useSession();
  const webAccounts = useWebAccounts();
  // ONE ACCOUNT, BOTH PLACES: the switch on and an account signed in. Decided per render from the seam's live getters.
  const oneAccount = webAccounts === true && signedIn(session.auth);
  const reauthWith = oneAccount ? session.auth.reauthenticateWith : null;
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState("");
  const [reentryRefusal, setReentryRefusal] = useState<AuthRefusal | null>(null);
  const account = useCall(() => session.transport.v2Credits.get(), [session.transport, session.revision]);
  const [ending, setEnding] = useState<AuthEnding | null>(null);
  const deletion = useAction(async (shownWarnings: string[]) => {
    // R-pending-ack-retention (P6, contract 2.31.0): the warning this screen showed, by hash (`app/shownCopy.ts`), for the
    // deletion's kept note — read by the server only with its DELETION_ACK_RECORD_ENABLED on.
    const result = await session.transport.v2Credits.deleteAccount(shownWarnings.length > 0 ? { shownWarnings } : {});
    // THE ORDER IS THE POINT: the server's purge answered 200, and only then does the sign-in go — and the appeal id
    // this tab kept (WEB-APPEAL-ID), which named an appeal the purge just removed.
    if (result.ok) {
      session.forgetAppeal();
      setEnding(await session.auth.endAfterDeletion({ signInDeleted: result.response.body.signInDeleted }));
    }
    return result;
  });

  /** C6 first (switch on, an account), then Apple's revocation while the user exists, then the server. */
  const confirmDelete = async () => {
    setReentryRefusal(null);
    if (reauthWith !== null) {
      const reauth = reauthWith === "apple"
        ? await session.auth.reauthenticate({ apple: true })
        : await session.auth.reauthenticate({ password });
      if (!reauth.ok) {
        setReentryRefusal(reauth.refusal);
        return;
      }
      await session.auth.revokeAppleBeforeDeletion();
    }
    const credits = account.status === "ok" ? account.response.body.credits : 0;
    await deletion.run(await hashesOf(deleteWarningTexts({ oneAccount, credits })));
  };

  if (deletion.state.status === "ok") {
    return (
      <div data-account-deleted data-auth-ended={ending ?? "pending"}>
        <h1 data-app-copy="account.navTitle">{afterText("account.navTitle")}</h1>
        <HeldLine id="account.delete.done" />
        <Actions>
          <button className="btn primary" type="button" onClick={() => session.leave("/")}>
            Continue
          </button>
        </Actions>
      </div>
    );
  }

  if (account.status === "pending") {
    return (
      <p className="spinner-line" role="status">
        Loading your account…
      </p>
    );
  }
  if (account.status === "failed") return <Refusal failure={account.failure} context="Your account" />;

  const credits = account.response.body.credits;

  return (
    <div className="delete-account" data-one-account={oneAccount}>
      <h1 data-app-copy="account.navTitle">{afterText("account.navTitle")}</h1>

      {credits > 0 && (
        <div className="pay" data-unused-web-credits={credits}>
          <div className="kv">
            <span>Credits on your account</span>
            <span data-credits={credits}>{credits}</span>
          </div>
          {oneAccount ? <HeldLine id="account.k.deleteRemovesEverywhere" /> : <HeldLine id="account.delete.unusedCredits" />}
        </div>
      )}

      {deletion.state.status === "failed" && <Refusal failure={deletion.state.failure} context="Deleting your account" />}

      {!confirming ? (
        <section className="card" data-delete-section>
          <Actions>
            <button className="btn" type="button" onClick={() => setConfirming(true)} data-delete-start data-app-copy="account.delete.button">
              {afterText("account.delete.button")}
            </button>
          </Actions>
          {/* DA-1: the app's footer under "Delete account" (AccountManagementView.swift:83, K12 since R175); the app's former words with the switch off. */}
          {oneAccount ? (
            <Say id="account.k.deleteFooter" className="field-foot" />
          ) : (
            <p className="field-foot" data-app-copy="account.delete.footer">{afterText("account.delete.footer")}</p>
          )}
        </section>
      ) : (
        <div className="card" role="alertdialog" aria-labelledby="delete-confirm-title" data-delete-confirm>
          <h3 id="delete-confirm-title" data-app-copy="account.delete.alertTitle">{afterText("account.delete.alertTitle")}</h3>
          {oneAccount ? <HeldLine id="account.k.deleteConfirm" /> : <HeldLine id="account.delete.confirm" />}
          {reauthWith === "password" && (
            <div className="reentry" data-reentry="password">
              <Say id="account.error.reauthPassword" className="field-foot" />
              <PasswordField value={password} onChange={setPassword} autoComplete="current-password" />
            </div>
          )}
          {reauthWith === "apple" && (
            <div className="reentry" data-reentry="apple">
              <Say id="account.error.reauthApple" className="field-foot" />
            </div>
          )}
          <AuthError refusal={reentryRefusal} reauthWith={reauthWith} />
          <Actions>
            <button
              className="btn"
              type="button"
              onClick={() => setConfirming(false)}
              disabled={deletion.state.status === "pending"}
              data-app-copy="account.delete.cancel"
            >
              {afterText("account.delete.cancel")}
            </button>
            {reauthWith === "apple" ? (
              <AppleButton
                copyId="account.signIn.apple"
                onClick={() => void confirmDelete()}
                disabled={deletion.state.status === "pending"}
                busy={deletion.state.status === "pending"}
              />
            ) : (
              <button
                className="btn primary"
                type="button"
                disabled={deletion.state.status === "pending" || (reauthWith === "password" && password === "")}
                onClick={() => void confirmDelete()}
                data-delete-confirm-button
                data-app-copy="account.delete.confirm"
              >
                {deletion.state.status === "pending" ? "Deleting…" : afterText("account.delete.confirm")}
              </button>
            )}
          </Actions>
        </div>
      )}
    </div>
  );
}
